import { useEffect, useState } from 'react';
import { useDeterministicColor } from '../../../hooks/useDeterministicColor';
import { avatarImageUrl } from '../../../lib/avatar-src';
import type { WorkStatusTone } from '../../../lib/work-status';
import './Avatar.css';

// DB raw avatarUrl 의 업로드 센티널 (`wcavatar:{ts}`/legacy `wcfile:{id}`) — 그대로 <img> 에
// 넣으면 항상 로드 실패라 이니셜로 강등됐다 (2026-06-10 채팅 목록 사진 미표시 원인).
// 센티널만 서버 스트림 URL 로 해석하고, http/blob/data 등 나머지 src 는 기존 동작 그대로 통과.
const WC_SENTINEL = /^wc(?:file|avatar):/;

export interface AvatarProps {
  userId: number;
  name: string;
  src?: string | null;
  /** xxs = 캘린더 월간 칩(높이 19px)용 초소형. 이니셜 1자 + 큰 폰트비로 가독성 확보. */
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showStatus?: boolean;
  status?: 'online' | 'away' | 'offline';
  /**
   * 근무 상태 색 계열(2026-08-12) — 주면 점 색이 `status` 대신 이 값을 따른다.
   * 상태바/드롭다운의 `is-<tone>` 과 **같은 이름·같은 색 변수**를 쓴다(lib/work-status).
   * 미지정이면 종전 동작 그대로(`status` 3색) — 기존 호출부 무영향.
   */
  tone?: WorkStatusTone;
  /** 점의 aria-label 대체값(예: '회의중'). 미지정이면 종전대로 `status` 문자열. */
  statusLabel?: string;
}

const SIZE_PX: Record<NonNullable<AvatarProps['size']>, number> = {
  xxs: 14,
  xs: 24,
  sm: 32,
  md: 40,
  lg: 56,
  xl: 96,
};

export function Avatar({
  userId, name, src, size = 'md', showStatus = false, status = 'offline', tone, statusLabel,
}: AvatarProps) {
  const resolvedSrc = src && WC_SENTINEL.test(src) ? avatarImageUrl(userId, src) : src;
  const px = SIZE_PX[size];
  // 14px 원에 두 글자는 못 읽는다 — xxs 만 1자 + 폰트비 상향. 기존 사이즈는 그대로.
  const tiny = size === 'xxs';
  const initials = name.slice(0, tiny ? 1 : 2);
  const bg = useDeterministicColor(userId);
  // 이미지 로드 실패(presigned 만료·403·고아 객체) 시 이니셜 폴백 (design-20260606 §16-C8).
  // src 변경 시 에러 상태 초기화 — 새 src 는 다시 시도.
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [resolvedSrc]);

  return (
    <span className="avatar" style={{ width: px, height: px, fontSize: px * (tiny ? 0.62 : 0.4) }}>
      {resolvedSrc && !imgError ? (
        <img src={resolvedSrc} alt={name} width={px} height={px} loading="lazy" onError={() => setImgError(true)} />
      ) : (
        <span className="avatar-initials" style={{ background: bg }}>
          {initials}
        </span>
      )}
      {showStatus && (
        <span
          // tone 이 있으면 근무 상태 색(상태바와 동일), 없으면 종전 3색 그대로.
          className={`avatar-status ${tone ? `avatar-status--${tone}` : `avatar-status-${status}`}`}
          aria-label={statusLabel ?? status}
        />
      )}
    </span>
  );
}
